import { FC, PropsWithChildren, useCallback, useLayoutEffect, useRef } from 'react';
import classnames from 'classnames';

import { requestAnimation } from '@hh.ru/magritte-common-func-utils';
import { Divider } from '@hh.ru/magritte-ui-divider';
import { ModalHeader } from '@hh.ru/magritte-ui-modal/ModalHeader';
import { ModalHeaderProps } from '@hh.ru/magritte-ui-modal/types';

import styles from './modal.less';

const checkIsScrolledToBottom = (el: HTMLElement) => {
    return Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 1;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// Минимальная высота до которой может сжаться хедер
const HEADER_SHRINK_MIN_HEIGHT = 148;
// Минимальная высота отступа между хедером и контентом
const HEADER_MARGIN_MIN_HEIGHT = 12;
// Максимальная высота отступа между хедером и контентом
const HEADER_MARGIN_MAX_HEIGHT = 24;
const HEADER_MARGIN_HEIGHT_DELTA = HEADER_MARGIN_MAX_HEIGHT - HEADER_MARGIN_MIN_HEIGHT;

// Вычисляет минимальную высоту до которой может сжаться хедер с учетом высоты контента, чтобы не спровоцировать
// изменение размера модалки
const calcHeaderShrinkLimit = (
    headerHeight: number,
    headerMaxHeight: number,
    contentHeight: number,
    contentScrollHeight: number,
    marginHeight: number
) => {
    // Высота контента скрытого под скроллом когда хедер и марджин под ним имеет максимальную допустимую высоту
    const contentScrollDelta =
        contentScrollHeight - // Высота контента скрытого под скроллом в текущем состоянии
        contentHeight + // Высота контейнера содержащего контент
        (HEADER_MARGIN_MAX_HEIGHT - marginHeight) + // Дельта между текущей высотой марджина и максимальной допустимой
        (headerMaxHeight - headerHeight); // Дельта между текущей высотой хедера и максимальной допустимой

    const headerShrinkMinHeight = headerMaxHeight - (contentScrollDelta - HEADER_MARGIN_HEIGHT_DELTA);

    return Math.max(headerShrinkMinHeight, HEADER_SHRINK_MIN_HEIGHT);
};

const shrinkByDelta = (
    headerImageContainer: HTMLDivElement | null,
    headerWrapper: HTMLDivElement | null,
    headerMinHeight: number,
    headerMaxHeight: number,
    scrollDeltaY: number
) => {
    if (!headerImageContainer || !headerWrapper) {
        return;
    }

    const marginHeight = clamp(
        HEADER_MARGIN_MAX_HEIGHT - scrollDeltaY,
        HEADER_MARGIN_MIN_HEIGHT,
        HEADER_MARGIN_MAX_HEIGHT
    );
    headerWrapper.style.marginBottom = `${marginHeight}px`;

    const headerHeight = clamp(
        // Тут прибаляем дельту марджина потому что сначала должен сжаться отступ
        Math.floor(headerMaxHeight - scrollDeltaY + HEADER_MARGIN_HEIGHT_DELTA),
        headerMinHeight,
        headerMaxHeight
    );
    headerImageContainer.style.height = `${headerHeight}px`;
};

export const ModalContentWithHeader: FC<
    PropsWithChildren<ModalHeaderProps & { labelId: string; isTreeSelectorChild: boolean }>
> = ({ labelId, children, headerHeight: _headerHeight = 0, isTreeSelectorChild, ...modalHeaderProps }) => {
    const { title, headerImage, actions, actionLink, options } = modalHeaderProps;

    const bottomDividerVisibleRef = useRef(false);
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const headerImageContainerRef = useRef<HTMLDivElement>(null);
    const headerWrapperRef = useRef<HTMLDivElement>(null);
    const spacePreserverRef = useRef<HTMLDivElement>(null);
    const stickyContainerRef = useRef<HTMLDivElement>(null);
    const headerHeightRef = useRef(0);
    const minHeaderHeightRef = useRef(0);
    const headerImageRef = useRef(!!headerImage);
    headerHeightRef.current = headerImage ? Math.max(_headerHeight, 200) : 0;
    headerImageRef.current = !!headerImage;

    const bottomDividerContainerRef = useRef<HTMLDivElement>(null);
    const topDividerContainerRef = useRef<HTMLDivElement>(null);

    const withoutHeader = !title && !headerImage && !actions && !actionLink && !options;
    const onlyActions = !title && !headerImage && !options && (actions || actionLink);

    const isTopDividerVisible = () => {
        if (!contentContainerRef.current) {
            return false;
        }

        const topDividerVisible = headerImageRef.current
            ? contentContainerRef.current.scrollTop >
              headerHeightRef.current - minHeaderHeightRef.current + HEADER_MARGIN_HEIGHT_DELTA
            : contentContainerRef.current.scrollTop !== 0;

        return topDividerVisible && (options || !headerImage) && !onlyActions && !withoutHeader;
    };

    const updateHeaderDimensions = useCallback(() => {
        if (
            !headerImageContainerRef.current ||
            !headerWrapperRef.current ||
            !stickyContainerRef.current ||
            !spacePreserverRef.current ||
            !contentContainerRef.current
        ) {
            return;
        }

        if (!headerImageRef.current) {
            spacePreserverRef.current.style.height = '0';
            return;
        }

        minHeaderHeightRef.current = calcHeaderShrinkLimit(
            headerImageContainerRef.current.clientHeight,
            headerHeightRef.current,
            contentContainerRef.current.clientHeight,
            stickyContainerRef.current.scrollHeight,
            parseInt(headerWrapperRef.current.style.marginBottom || '0', 10)
        );

        shrinkByDelta(
            headerImageContainerRef.current,
            headerWrapperRef.current,
            minHeaderHeightRef.current,
            headerHeightRef.current,
            contentContainerRef.current.scrollTop
        );
        const preserverSize = headerHeightRef.current - minHeaderHeightRef.current + HEADER_MARGIN_HEIGHT_DELTA;
        spacePreserverRef.current.style.height = `${preserverSize}px`;
    }, []);

    useLayoutEffect(() => {
        if (contentContainerRef.current) {
            const observer = new ResizeObserver(
                requestAnimation(() => {
                    if (!contentContainerRef.current) {
                        return;
                    }
                    const isScrollable =
                        contentContainerRef.current.scrollHeight > contentContainerRef.current.clientHeight;
                    const hasScroll = isScrollable && !checkIsScrolledToBottom(contentContainerRef.current);
                    bottomDividerVisibleRef.current = hasScroll;
                    bottomDividerContainerRef.current?.classList.toggle(
                        styles.dividerContainerHidden,
                        !bottomDividerVisibleRef.current
                    );
                    updateHeaderDimensions();
                })
            );
            observer.observe(contentContainerRef.current);
            return () => observer.disconnect();
        }

        return undefined;
    }, [updateHeaderDimensions]);

    useLayoutEffect(() => {
        updateHeaderDimensions();
        if (!options && headerImage) {
            topDividerContainerRef.current?.classList.toggle(styles.dividerContainerHidden, true);
        }
    }, [headerImage, updateHeaderDimensions, options]);

    const handleScroll = () => {
        if (!contentContainerRef.current) {
            return;
        }

        bottomDividerVisibleRef.current = !checkIsScrolledToBottom(contentContainerRef.current);
        bottomDividerContainerRef.current?.classList.toggle(
            styles.dividerContainerHidden,
            !bottomDividerVisibleRef.current
        );
        topDividerContainerRef.current?.classList.toggle(styles.dividerContainerHidden, !isTopDividerVisible());
        if (headerImageRef.current) {
            shrinkByDelta(
                headerImageContainerRef.current,
                headerWrapperRef.current,
                minHeaderHeightRef.current,
                headerHeightRef.current,
                contentContainerRef.current.scrollTop
            );
        }
    };

    return (
        <>
            <div
                className={classnames(styles.modalContentWrapper, {
                    [styles.modalContentWrapperOneLine]: onlyActions,
                })}
            >
                <ModalHeader
                    {...modalHeaderProps}
                    headerHeight={headerHeightRef.current}
                    labelId={labelId}
                    wrapperRef={headerWrapperRef}
                    ref={headerImageContainerRef}
                />
                <div
                    className={classnames(styles.dividerContainer, {
                        [styles.dividerContainerHidden]: !isTopDividerVisible(),
                    })}
                    ref={topDividerContainerRef}
                >
                    <Divider />
                </div>
                <div
                    className={classnames(styles.modalContent)}
                    ref={contentContainerRef}
                    onScroll={handleScroll}
                    data-qa="modal-content-scroll-container"
                >
                    {isTreeSelectorChild ? (
                        children
                    ) : (
                        <>
                            <div className={styles.contentStickyContainer} ref={stickyContainerRef}>
                                {children}
                            </div>
                            <div ref={spacePreserverRef} />
                        </>
                    )}
                </div>
            </div>
            <div
                className={classnames(styles.dividerContainer, {
                    [styles.dividerContainerHidden]: !bottomDividerVisibleRef.current,
                })}
                ref={bottomDividerContainerRef}
            >
                <Divider />
            </div>
        </>
    );
};
